import React, { useState, useEffect } from "react";
import { Table, Form, Button, Badge, Modal, Pagination } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../../../components/Layouts/Layouts/Layout";
import DashboardHeader from "../../../components/basic/DashboardHeader";
import { getURLbyEndPoint } from "../../../store/api";

const getEmailTemplate = (companyName, validUpTo) => {
    const expiryDate = validUpTo
        ? new Date(validUpTo.replace(/-/g, "/")).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "long",
            year: "numeric",
        })
        : "N/A";

    return `
        <!DOCTYPE html>
        <html lang="en">
        <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
            body {
            margin: 0;
            padding: 0;
            font-family: "Helvetica", sans-serif;
            background-color: #f4f4f4;
            }
        </style>
        </head>
        <body>       
        <p>Dear Esteemed Collaborator,</p>
        <p>Greetings from AECCI!!!</p>
        <p>We sincerely value your association and collaboration with AECCI. Our international collaborators play an important role in creating meaningful opportunities between Indian businesses and the global business community.</p>
        <p>Our records indicate that your Collaboration Agreement with AECCI is due for renewal.</p>
        <p>We kindly request you to renew your collaboration agreement at your earliest convenience to continue the association with AECCI and participate in our international trade and business initiatives.</p>
        <p>Through the renewed collaboration, you may continue to be part of: our new initiative AECCI Global Deal Room and reach a wider business community.</p>
        <p>Also, this gives the collaborators an access to AECCI's international trade facilitation and arbitration ecosystem</p>
        <p>AECCI continues to expand its international network with the objective of facilitating trusted, practical and result-oriented business connections between India and global markets. Your continued association as an AECCI Collaborator strengthens this network and enables us to jointly create greater opportunities for businesses across borders.</p>
        <p>Please login to your account and click on the renewal button given against your collaboration number enabling he AECCI team to complete the renewal formalities and receive the updated collaboration agreement.</p>
        <p>For any clarification or assistance regarding the renewal process, please feel free to contact us. Thank you for your continued trust, support and association with AECCI.</p>
        <p>Warm regards,<br>
        AECCI Team<br>
        Asian Exporters' Chamber of Commerce & Industry<br>
        🌐 <a href="https://www.aecci.org.in/">https://www.aecci.org.in/</a></p>
        </body>
        </html>
    `;
};

const CollaboratorRenewals = () => {
    const navigate = useNavigate();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [membershipFilter, setMembershipFilter] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(15);
    const [sendingEmail, setSendingEmail] = useState({});

    // Email preview modal
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailModalData, setEmailModalData] = useState(null);
    const [emailSubject, setEmailSubject] = useState("");
    const [emailBody, setEmailBody] = useState("");
    const [emailFrom, setEmailFrom] = useState("legal@aecci.org.in");
    const [emailCC, setEmailCC] = useState("ed@aecci.org.in");

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                getURLbyEndPoint("getCollaboratorExpiryList")
            );
            if (response.data.status) {
                setMembers(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    // Get unique membership types for filter
    const membershipTypes = [
        ...new Set(members.map((m) => m.selectMembership)),
    ].sort();

    // Filtering
    const filteredData = members.filter((data) => {
        if (statusFilter) {
            if (statusFilter === "Requested") {
                if (!data.isRenewalRequested) return false;
            } else if (data.status !== statusFilter) {
                return false;
            }
        }
        if (membershipFilter && data.selectMembership !== membershipFilter)
            return false;
        if (
            searchTerm &&
            !data.companyName.toLowerCase().includes(searchTerm.toLowerCase()) &&
            !(data.memberShipNo || "")
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
        )
            return false;
        return true;
    });

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentFiltered = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
    const handlePrevPage = () =>
        setCurrentPage((prev) => Math.max(prev - 1, 1));
    const handleNextPage = () =>
        setCurrentPage((prev) => Math.min(prev + 1, totalPages));



    // Open email preview
    const handleOpenEmailPreview = (member) => {
        setEmailModalData(member);
        setEmailSubject(
            "Renewal of Collaboration Agreement with AECCI – Continuing Our Global Partnership"
        );
        setEmailBody(getEmailTemplate(member.companyName, member.validUpTo));
        setShowEmailModal(true);
    };

    // Send email
    const handleSendEmail = async () => {
        if (!emailModalData) return;

        setSendingEmail((prev) => ({ ...prev, [emailModalData._id]: true }));
        try {
            const res = await axios.post(
                getURLbyEndPoint("sendCollaboratorRenewalEmail") + emailModalData._id,
                {
                    subject: emailSubject,
                    emailBody: emailBody,
                    from: emailFrom,
                    cc: emailCC,
                }
            );
            if (res.data.status) {
                alert(res.data.message);
                setShowEmailModal(false);
                fetchData(); // Refresh the list to show the new email sent status
            } else {
                alert(res.data.message || "Failed to send email");
            }
        } catch (error) {
            console.error(error);
            alert("Failed to send email");
        } finally {
            setSendingEmail((prev) => ({ ...prev, [emailModalData._id]: false }));
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case "Expired":
                return <Badge bg="danger">Expired</Badge>;
            case "Expiring Soon":
                return <Badge bg="warning" text="dark">Expiring Soon</Badge>;
            case "Active":
                return <Badge bg="success">Active</Badge>;
            default:
                return <Badge bg="secondary">Unknown</Badge>;
        }
    };

    const getRenewalBadge = (isRenewalRequested) => {
        if (isRenewalRequested) {
            return <Badge bg="info">Requested</Badge>;
        }
        return <span className="text-muted">—</span>;
    };

    // Stats
    const expiredCount = members.filter((m) => m.status === "Expired").length;
    const expiringSoonCount = members.filter(
        (m) => m.status === "Expiring Soon"
    ).length;
    const activeCount = members.filter((m) => m.status === "Active").length;

    return (
        <>
            <Layout>
                <div className="d-flex justify-content-center align-items-start">
                    <div className="py-1 align-items-center justify-content-center main-width">
                        <hr />
                        <DashboardHeader
                            heading="Collaboration Portal / Collaborator Renewals"
                            backUrl="/master-collaboration-portal"
                        />
                        <hr />

                        {/* Stats Cards */}
                        <div className="d-flex gap-3 mb-3 flex-wrap">
                            <div
                                className="p-3 rounded text-center flex-fill"
                                style={{
                                    background: "#f8d7da",
                                    border: "1px solid #f5c6cb",
                                    minWidth: "150px",
                                }}
                            >
                                <h4 className="mb-0 text-danger">{expiredCount}</h4>
                                <small className="text-danger fw-semibold">Expired</small>
                            </div>
                            <div
                                className="p-3 rounded text-center flex-fill"
                                style={{
                                    background: "#fff3cd",
                                    border: "1px solid #ffeeba",
                                    minWidth: "150px",
                                }}
                            >
                                <h4 className="mb-0" style={{ color: "#856404" }}>
                                    {expiringSoonCount}
                                </h4>
                                <small className="fw-semibold" style={{ color: "#856404" }}>
                                    Expiring Soon (≤30 days)
                                </small>
                            </div>
                            <div
                                className="p-3 rounded text-center flex-fill"
                                style={{
                                    background: "#d4edda",
                                    border: "1px solid #c3e6cb",
                                    minWidth: "150px",
                                }}
                            >
                                <h4 className="mb-0 text-success">{activeCount}</h4>
                                <small className="text-success fw-semibold">Active</small>
                            </div>
                            <div
                                className="p-3 rounded text-center flex-fill"
                                style={{
                                    background: "#cce5ff",
                                    border: "1px solid #b8daff",
                                    minWidth: "150px",
                                }}
                            >
                                <h4 className="mb-0 text-primary">
                                    {members.filter((m) => m.isRenewalRequested).length}
                                </h4>
                                <small className="text-primary fw-semibold">Renewal Requested</small>
                            </div>
                            <div
                                className="p-3 rounded text-center flex-fill"
                                style={{
                                    background: "#e2e3e5",
                                    border: "1px solid #d6d8db",
                                    minWidth: "150px",
                                }}
                            >
                                <h4 className="mb-0">{members.length}</h4>
                                <small className="fw-semibold">Total Members</small>
                            </div>
                        </div>

                        {/* Filters */}
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                            <div className="d-flex gap-2 flex-wrap">
                                <Form.Check
                                    inline
                                    label="All"
                                    name="statusFilter"
                                    type="radio"
                                    checked={statusFilter === ""}
                                    onChange={() => {
                                        setStatusFilter("");
                                        setCurrentPage(1);
                                    }}
                                />
                                <Form.Check
                                    inline
                                    label="Expired"
                                    name="statusFilter"
                                    type="radio"
                                    checked={statusFilter === "Expired"}
                                    onChange={() => {
                                        setStatusFilter("Expired");
                                        setCurrentPage(1);
                                    }}
                                />
                                <Form.Check
                                    inline
                                    label="Expiring Soon"
                                    name="statusFilter"
                                    type="radio"
                                    checked={statusFilter === "Expiring Soon"}
                                    onChange={() => {
                                        setStatusFilter("Expiring Soon");
                                        setCurrentPage(1);
                                    }}
                                />
                                <Form.Check
                                    inline
                                    label="Active"
                                    name="statusFilter"
                                    type="radio"
                                    checked={statusFilter === "Active"}
                                    onChange={() => {
                                        setStatusFilter("Active");
                                        setCurrentPage(1);
                                    }}
                                />
                                <Form.Check
                                    inline
                                    label="Requested"
                                    name="statusFilter"
                                    type="radio"
                                    checked={statusFilter === "Requested"}
                                    onChange={() => {
                                        setStatusFilter("Requested");
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>
                            <div className="d-flex gap-2 flex-wrap">
                                <Form.Select
                                    size="sm"
                                    value={membershipFilter}
                                    onChange={(e) => {
                                        setMembershipFilter(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    style={{ width: "200px" }}
                                >
                                    <option value="">All Memberships</option>
                                    {membershipTypes.map((type) => (
                                        <option key={type} value={type}>
                                            {type}
                                        </option>
                                    ))}
                                </Form.Select>
                                <input
                                    className="form-control form-control-sm"
                                    type="search"
                                    placeholder="Search company or membership no."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    style={{ width: "250px" }}
                                />
                            </div>
                        </div>

                        <p className="text-muted mb-2">
                            Showing {filteredData.length} of {members.length} members
                        </p>

                        {/* Table */}
                        {loading ? (
                            <p>Loading...</p>
                        ) : members.length > 0 ? (
                            <Table
                                responsive
                                striped
                                bordered
                                hover
                                className="text-center"
                                size="sm"
                            >
                                <thead>
                                    <tr>
                                        <th className="align-middle">SR</th>
                                        <th className="align-middle">Company Name</th>
                                        <th className="align-middle">Membership No.</th>
                                        <th className="align-middle">Type</th>
                                        <th className="align-middle">Valid Upto</th>
                                        <th className="align-middle">Days Left</th>
                                        <th className="align-middle">Status</th>
                                        <th className="align-middle">Renewal</th>
                                        <th className="align-middle">Email</th>
                                        <th className="align-middle">Reminder</th>
                                        <th className="align-middle">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentFiltered.length > 0 ? (
                                        currentFiltered.map((data, index) => (
                                            <tr key={data._id}>
                                                <td>{indexOfFirstItem + index + 1}</td>
                                                <td className="text-start" style={{ maxWidth: "200px" }}>
                                                    <small className="fw-semibold">{data.companyName}</small>
                                                </td>
                                                <td>
                                                    <small>{data.memberShipNo || "—"}</small>
                                                </td>
                                                <td>
                                                    <small>{data.selectMembership}</small>
                                                </td>
                                                <td>
                                                    <small>
                                                        {data.validUpTo
                                                            ? new Date(
                                                                data.validUpTo.replace(/-/g, "/")
                                                            ).toLocaleDateString("en-GB")
                                                            : "—"}
                                                    </small>
                                                </td>
                                                <td>
                                                    <small
                                                        className={
                                                            data.daysLeft <= 0
                                                                ? "text-danger fw-bold"
                                                                : data.daysLeft <= 30
                                                                    ? "text-warning fw-bold"
                                                                    : "text-success"
                                                        }
                                                    >
                                                        {data.daysLeft !== null ? data.daysLeft : "—"}
                                                    </small>
                                                </td>
                                                <td>{getStatusBadge(data.status)}</td>
                                                <td>{getRenewalBadge(data.isRenewalRequested)}</td>
                                                <td>
                                                    <small>{data.email}</small>
                                                </td>
                                                <td>
                                                    {data.latestEmail ? (
                                                        <div className="d-flex flex-column align-items-center">
                                                            <Badge bg="success" className="mb-1">
                                                                Sent
                                                            </Badge>
                                                            <small style={{ fontSize: "10px", color: "#666" }}>
                                                                {new Date(data.latestEmail).toLocaleDateString("en-GB")}
                                                            </small>
                                                        </div>
                                                    ) : (
                                                        <Badge bg="secondary">Not Sent</Badge>
                                                    )}
                                                </td>
                                                <td className="d-flex flex-column gap-2 align-items-center">
                                                    <Button
                                                        size="sm"
                                                        variant="primary"
                                                        disabled={sendingEmail[data._id]}
                                                        onClick={() => handleOpenEmailPreview(data)}
                                                        className="w-100"
                                                    >
                                                        {sendingEmail[data._id]
                                                            ? "Sending..."
                                                            : "📧 Send"}
                                                    </Button>
                                                    {data.isRenewalRequested && (
                                                        <Button
                                                            size="sm"
                                                            variant="success"
                                                            className="w-100"
                                                            onClick={() => navigate('/master-profile-super-admin', { state: { companyName: data.companyName, collabRenewal: true } })}
                                                        >
                                                            Approve
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={11} className="text-center">
                                                No Matching Data
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        ) : (
                            <p>No data available</p>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="d-flex justify-content-start mt-2">
                                <Pagination size="sm">
                                    <Pagination.Prev
                                        onClick={handlePrevPage}
                                        disabled={currentPage === 1}
                                    />
                                    {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                                        const startPage = Math.max(
                                            1,
                                            Math.min(currentPage - 5, totalPages - 9)
                                        );
                                        const page = startPage + i;
                                        if (page > totalPages) return null;
                                        return (
                                            <Pagination.Item
                                                key={page}
                                                active={page === currentPage}
                                                onClick={() => handlePageChange(page)}
                                            >
                                                {page}
                                            </Pagination.Item>
                                        );
                                    })}
                                    <Pagination.Next
                                        onClick={handleNextPage}
                                        disabled={currentPage === totalPages}
                                    />
                                </Pagination>
                            </div>
                        )}
                    </div>
                </div>
            </Layout>

            {/* Email Preview Modal */}
            <Modal
                show={showEmailModal}
                onHide={() => setShowEmailModal(false)}
                size="xl"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        Email Preview — {emailModalData?.companyName}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex gap-3 mb-3">
                        <div className="flex-fill">
                            <Form.Label className="fw-semibold">To:</Form.Label>
                            <Form.Control type="text" value={emailModalData?.email || ""} disabled size="sm" />
                        </div>
                        <div className="flex-fill">
                            <Form.Label className="fw-semibold">CC:</Form.Label>
                            <Form.Select
                                size="sm"
                                value={emailCC}
                                onChange={(e) => setEmailCC(e.target.value)}
                            >
                                <option value="ed@aecci.org.in">ed@aecci.org.in</option>
                                <option value="legal@aecci.org.in">legal@aecci.org.in</option>
                            </Form.Select>
                        </div>
                        <div className="flex-fill">
                            <Form.Label className="fw-semibold">From:</Form.Label>
                            <Form.Select
                                size="sm"
                                value={emailFrom}
                                onChange={(e) => setEmailFrom(e.target.value)}
                            >
                                <option value="legal@aecci.org.in">legal@aecci.org.in</option>
                                <option value="ed@aecci.org.in">ed@aecci.org.in</option>
                            </Form.Select>
                        </div>
                    </div>
                    <div className="mb-3">
                        <Form.Label className="fw-semibold">Subject:</Form.Label>
                        <Form.Control
                            type="text"
                            value={emailSubject}
                            onChange={(e) => setEmailSubject(e.target.value)}
                        />
                    </div>
                    <div className="mb-3">
                        <Form.Label className="fw-semibold">Preview:</Form.Label>
                        <div
                            contentEditable
                            suppressContentEditableWarning
                            onBlur={(e) => setEmailBody(e.currentTarget.innerHTML)}
                            style={{
                                border: "1px solid #ddd",
                                borderRadius: "8px",
                                padding: "16px",
                                maxHeight: "400px",
                                overflow: "auto",
                                background: "#fff",
                            }}
                            dangerouslySetInnerHTML={{ __html: emailBody }}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowEmailModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="success"
                        onClick={handleSendEmail}
                        disabled={sendingEmail[emailModalData?._id]}
                    >
                        {sendingEmail[emailModalData?._id]
                            ? "Sending..."
                            : "✉️ Send Email"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CollaboratorRenewals;
